import { useState, useEffect, useCallback, useRef } from "react";
import { inventoryItemDtlApi, productApi, vendorApi } from "../api/api";
import { formatDateTime } from "../utils/dateUtils";

/* ══════════════════════════════════════════════════════════
   GLOBAL BARCODE SCANNER POPUP
   ──────────────────────────────────────────────────────────
   Drop this ONE component anywhere in your app (e.g. App.jsx
   or InventoryItemPage.jsx). It needs no props.

   How it works:
   • Listens for keystrokes GLOBALLY (no input needs focus)
   • Barcode guns type all characters in < 50 ms then press Enter
   • When that pattern is detected → fetches item → shows popup
   • Works on ANY page the user is on

   Usage — add once in App.jsx:
     import BarcodeScanPopup from "./components/BarcodeScanPopup";
     <BarcodeScanPopup />        ← that's it, one line
══════════════════════════════════════════════════════════ */

const fmt = (v) => (parseFloat(v) || 0).toFixed(3);

/* pre-load master data once and cache in module scope */
let cachedProducts = null;
let cachedVendors  = null;

async function getMasterData() {
  if (!cachedProducts) {
    const [p, v] = await Promise.all([productApi.getAll(), vendorApi.getAll()]);
    cachedProducts = Array.isArray(p) ? p : (p?.data ?? []);
    cachedVendors  = Array.isArray(v) ? v : (v?.data ?? []);
  }
  return { products: cachedProducts, vendors: cachedVendors };
}

export default function BarcodeScanPopup() {
  const [state, setState] = useState("idle"); // idle | loading | found | error
  const [result, setResult] = useState(null); // { item, product, vendor }
  const [errMsg, setErrMsg] = useState("");

  /* barcode gun buffer */
  const bufferRef   = useRef("");
  const lastKeyTime = useRef(0);
  const timerRef    = useRef(null);

  const lookup = useCallback(async (raw) => {
    const id = parseInt(raw.trim(), 10);
    if (!id || isNaN(id)) return;

    setState("loading");
    setResult(null);

    try {
      const { products, vendors } = await getMasterData();
      let item = null;
      try {
        item = await inventoryItemDtlApi.getById(id);
      } catch (err) {
        // Fallback: search all inventory items by barcodeID or inventoryItemID
        const allRes = await inventoryItemDtlApi.getAll().catch(() => []);
        const allItems = Array.isArray(allRes) ? allRes : (allRes?.data ?? []);
        item = allItems.find((i) => Number(i.inventoryItemID) === id || Number(i.barcodeID) === id) || null;
        if (!item) throw err;
      }
      const product = products.find((p) => p.productID === item.productID) ?? null;
      const vendor  = vendors.find((v)  => v.vendorID  === product?.vendorID) ?? null;
      setResult({ item, product, vendor });
      setState("found");
    } catch (err) {
      setErrMsg(
        err.response?.status === 404
          ? `Item #${id} not found`
          : "Lookup failed — check connection"
      );
      setState("error");
    }
  }, []);

  const handleCreateBill = () => {
    if (!result?.item) return;
    if ((Number(result.item.quantity) || 0) <= 0) {
      alert(`Cannot create bill: Tag #${result.item.inventoryItemID} is already marked as sold.`);
      return;
    }
    sessionStorage.setItem("reva_scanned_item_for_invoice", JSON.stringify(result.item));
    window.dispatchEvent(new CustomEvent("reva:navigate", { detail: "Invoice" }));
    window.dispatchEvent(new CustomEvent("reva:open_invoice_with_item", { detail: result.item }));
    close();
  };

  const handleViewInStock = () => {
    if (!result?.item) return;
    sessionStorage.setItem("reva_selected_stock_item", String(result.item.inventoryItemID));
    window.dispatchEvent(new CustomEvent("reva:navigate", { detail: "Stocks" }));
    window.dispatchEvent(new CustomEvent("reva:filter_stock_item", { detail: String(result.item.inventoryItemID) }));
    close();
  };

  /* ── global keydown listener ── */
  useEffect(() => {
    const onKey = (e) => {
      const el = document.activeElement;
      const tag = el?.tagName;

      /* ignore if user is typing in an input / textarea / select */
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      /* ignore if typing inside contenteditable or rich text editors (Quill, etc.) */
      if (el?.isContentEditable || el?.closest?.('[contenteditable="true"], .ql-editor, .ql-container')) {
        return;
      }

      /* ignore if any modal/dialog is currently active on screen */
      if (
        el?.closest?.('.rv-modal, .rv-dialog, .bsp-popup') ||
        document.querySelector('.rv-modal-backdrop, .rv-dialog-backdrop, .rv-pub-modal-backdrop')
      ) {
        return;
      }

      const now  = Date.now();
      const gap  = now - lastKeyTime.current;
      lastKeyTime.current = now;

      if (e.key === "Enter") {
        const val = bufferRef.current.trim();
        bufferRef.current = "";
        clearTimeout(timerRef.current);
        if (val.length >= 1) lookup(val);
        return;
      }

      /* only collect printable chars */
      if (e.key.length !== 1) return;

      if (gap > 500) {
        /* long gap = new scan starting */
        bufferRef.current = e.key;
      } else {
        bufferRef.current += e.key;
      }

      /* auto-fire after 150 ms silence (some guns don't send Enter) */
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const val = bufferRef.current.trim();
        bufferRef.current = "";
        if (val.length >= 2) lookup(val);
      }, 150);
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(timerRef.current);
    };
  }, [lookup]);

  const close = () => {
    setState("idle");
    setResult(null);
    setErrMsg("");
  };

  /* nothing to show */
  if (state === "idle") return null;

  const { item, product, vendor } = result ?? {};

  return (
    <div className="bsp-backdrop" onClick={close}>
      <div className="bsp-popup" onClick={(e) => e.stopPropagation()}>

        {/* ── Loading ── */}
        {state === "loading" && (
          <div className="bsp-center">
            <span className="bsp-big-spin" />
            <p className="bsp-loading-txt">Looking up item…</p>
          </div>
        )}

        {/* ── Error ── */}
        {state === "error" && (
          <div className="bsp-center">
            <div className="bsp-err-icon"><AlertIcon /></div>
            <p className="bsp-err-txt">{errMsg}</p>
            <button className="bsp-close-btn" onClick={close}>Close</button>
          </div>
        )}

        {/* ── Result ── */}
        {state === "found" && item && (
          <>
            {/* Header */}
            <div className="bsp-header">
              <div className="bsp-avatar"><BoxIcon /></div>
              <div className="bsp-header-text">
                <p className="bsp-product-name">
                  {product?.productName ?? `Product #${item.productID}`}
                </p>
                <div className="bsp-chips">
                  <span className="bsp-chip bsp-chip-id">Item #{item.inventoryItemID}</span>
                  {product?.productCode && (
                    <span className="bsp-chip bsp-chip-code">{product.productCode}</span>
                  )}
                  {product?.purityStandard && (
                    <span className="bsp-chip bsp-chip-purity">{product.purityStandard}</span>
                  )}
                </div>
              </div>
              <button className="bsp-x" onClick={close}><XIcon /></button>
            </div>

            {/* Grid */}
            <div className="bsp-grid">

              {/* weights */}
              <div className="bsp-section">
                <p className="bsp-sec-title"><WeightIcon /> Weights</p>
                <Row label="Gross"  value={item.grossWeight ? `${item.grossWeight} g` : "—"} />
                <Row label="Less"   value={item.lessWeight  ? `${item.lessWeight} g`  : "—"} />
                <Row label="Net"    value={`${fmt(item.netWeight || item.grossWeight - item.lessWeight)} g`} highlight />
                <Row label="Qty"    value={item.quantity ?? "—"} />
              </div>

              {/* product */}
              <div className="bsp-section">
                <p className="bsp-sec-title"><ProductIcon /> Product</p>
                <Row label="Code"    value={product?.productCode    ?? "—"} mono />
                <Row label="Purity"  value={product?.purityStandard ?? "—"} />
                <Row label="Pricing" value={product?.pricingType    ?? "—"} />
                <Row label="HSN"     value={product?.codeHSN        ?? "—"} mono />
              </div>

              {/* vendor */}
              <div className="bsp-section">
                <p className="bsp-sec-title"><VendorIcon /> Vendor</p>
                <Row label="Name"     value={vendor?.vendorName ?? "—"} />
                <Row label="In-House" value={vendor ? (vendor.isInHouse ? "Yes" : "No") : "—"} />
                <Row label="Phone"    value={vendor?.phoneNumber ?? "—"} />
                <Row label="Stock #"  value={item.stockID ? `#${item.stockID}` : "—"} mono />
              </div>

            </div>

            {/* Footer */}
            <div className="bsp-footer">
              {item.lastUPDTTM && (
                <span className="bsp-updated">
                  Updated: {formatDateTime(item.lastUPDTTM)}
                </span>
              )}
              <div className="bsp-actions">
                <button className="bsp-btn bsp-btn--stock" onClick={handleViewInStock}>
                  <BoxIcon /> View in Stocks
                </button>
                <button className="bsp-btn bsp-btn--bill" onClick={handleCreateBill}>
                  <InvoiceIcon /> Bill Item (Invoice)
                </button>
                <button className="bsp-close-btn" onClick={close}>Close</button>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        .bsp-backdrop {
          position: fixed; inset: 0; z-index: 99999;
          background: rgba(8, 22, 28, .52);
          backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
          animation: bsp-fade-in .15s ease;
        }
        @keyframes bsp-fade-in { from { opacity: 0 } to { opacity: 1 } }

        .bsp-popup {
          background: #fff;
          border-radius: 18px;
          width: 100%; max-width: 520px;
          max-height: 90vh; overflow-y: auto;
          box-shadow: 0 32px 80px rgba(8,22,28,.28), 0 0 0 1px rgba(126,207,219,.2);
          animation: bsp-slide-up .2s cubic-bezier(.34,1.56,.64,1);
        }
        @keyframes bsp-slide-up {
          from { opacity: 0; transform: translateY(20px) scale(.96) }
          to   { opacity: 1; transform: none }
        }

        /* loading / error center */
        .bsp-center {
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          padding: 48px 24px; gap: 16px;
        }
        .bsp-big-spin {
          width: 40px; height: 40px;
          border: 3.5px solid rgba(126,207,219,.25);
          border-top-color: #2a8a96;
          border-radius: 50%;
          animation: spin .7s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg) } }
        .bsp-loading-txt { font-size: 15px; color: #5a9ea8; font-weight: 600; margin: 0; }
        .bsp-err-icon svg { width: 36px; height: 36px; color: #e05c5c; }
        .bsp-err-txt { font-size: 15px; color: #c0392b; font-weight: 600; margin: 0; text-align: center; }

        /* header */
        .bsp-header {
          display: flex; align-items: center; gap: 13px;
          padding: 20px 20px 16px;
          background: linear-gradient(135deg, #d4f4f9 0%, #ecfcfd 100%);
          border-bottom: 1px solid rgba(126,207,219,.25);
        }
        .bsp-avatar {
          width: 44px; height: 44px; flex-shrink: 0;
          border-radius: 12px; background: #fff;
          display: flex; align-items: center; justify-content: center;
          color: #2a8a96;
          box-shadow: 0 2px 8px rgba(42,138,150,.15);
        }
        .bsp-avatar svg { width: 20px; height: 20px; }
        .bsp-product-name { font-size: 16px; font-weight: 800; color: #0d1a1c; margin: 0 0 6px; }
        .bsp-chips { display: flex; gap: 6px; flex-wrap: wrap; }
        .bsp-chip {
          font-size: 11px; font-weight: 700;
          padding: 3px 9px; border-radius: 20px;
        }
        .bsp-chip-id    { background: rgba(42,138,150,.12); color: #2a8a96; }
        .bsp-chip-code  { background: rgba(10,10,10,.07); color: #0d1a1c; font-family: 'Courier New', monospace; }
        .bsp-chip-purity{ background: rgba(234,179,8,.13); color: #b45309; }
        .bsp-x {
          margin-left: auto; background: rgba(255,255,255,.6);
          border: none; cursor: pointer; padding: 7px;
          border-radius: 8px; color: #5a9ea8; display: flex;
          transition: all .13s; flex-shrink: 0;
        }
        .bsp-x:hover { background: #fff; color: #2a8a96; }
        .bsp-x svg { width: 15px; height: 15px; }

        /* grid */
        .bsp-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          border-bottom: 1px solid rgba(126,207,219,.15);
        }
        .bsp-section {
          padding: 16px 18px;
          border-right: 1px solid rgba(126,207,219,.15);
        }
        .bsp-section:last-child { border-right: none; }
        .bsp-sec-title {
          display: flex; align-items: center; gap: 6px;
          font-size: 10.5px; font-weight: 700;
          color: #5a9ea8; text-transform: uppercase;
          letter-spacing: .6px; margin: 0 0 10px;
        }
        .bsp-sec-title svg { width: 12px; height: 12px; }

        /* rows */
        .bsp-row {
          display: flex; justify-content: space-between; align-items: baseline;
          padding: 5px 0;
          border-bottom: 1px solid rgba(126,207,219,.08);
        }
        .bsp-row:last-child { border-bottom: none; }
        .bsp-row-hl {
          background: rgba(212,244,249,.35);
          margin: 0 -4px; padding: 5px 4px;
          border-radius: 6px; border-bottom: none;
        }
        .bsp-row-lbl { font-size: 11px; color: #7a9ea8; font-weight: 500; }
        .bsp-row-val { font-size: 12.5px; font-weight: 700; color: #0d1a1c; text-align: right; }
        .bsp-row-val-hl { font-size: 15px; font-weight: 900; color: #2a8a96; }
        .bsp-row-mono { font-family: 'Courier New', monospace; font-size: 11.5px; }

        /* footer */
        .bsp-footer {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 20px; gap: 12px; flex-wrap: wrap;
        }
        .bsp-updated { font-size: 11px; color: #8aacb4; }
        .bsp-actions {
          display: flex; align-items: center; gap: 8px; margin-left: auto; flex-wrap: wrap;
        }
        .bsp-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px; border-radius: 9px;
          font-size: 12px; font-weight: 700; cursor: pointer;
          transition: all .15s ease; border: none; text-decoration: none;
        }
        .bsp-btn svg { width: 14px; height: 14px; }
        .bsp-btn--stock {
          background: rgba(42,138,150,.1);
          color: #2a8a96;
          border: 1px solid rgba(42,138,150,.25);
        }
        .bsp-btn--stock:hover {
          background: rgba(42,138,150,.18);
        }
        .bsp-btn--bill {
          background: linear-gradient(135deg, #b38743 0%, #8c6527 100%);
          color: #fff;
          box-shadow: 0 2px 8px rgba(179,135,67,.28);
        }
        .bsp-btn--bill:hover {
          box-shadow: 0 4px 12px rgba(179,135,67,.4);
          transform: translateY(-1px);
        }
        .bsp-close-btn {
          padding: 8px 18px; border-radius: 9px;
          background: #e2e8f0; color: #475569;
          border: none; cursor: pointer;
          font-size: 12.5px; font-weight: 600;
          transition: background .13s;
        }
        .bsp-close-btn:hover { background: #cbd5e1; color: #1e293b; }

        @media (max-width: 540px) {
          .bsp-grid { grid-template-columns: 1fr; }
          .bsp-section { border-right: none; border-bottom: 1px solid rgba(126,207,219,.15); }
          .bsp-actions { width: 100%; justify-content: stretch; }
          .bsp-actions .bsp-btn, .bsp-actions .bsp-close-btn { flex: 1; justify-content: center; text-align: center; }
        }
      `}</style>
    </div>
  );
}

/* tiny row helper */
function Row({ label, value, highlight, mono }) {
  return (
    <div className={`bsp-row ${highlight ? "bsp-row-hl" : ""}`}>
      <span className="bsp-row-lbl">{label}</span>
      <span className={`bsp-row-val ${highlight ? "bsp-row-val-hl" : ""} ${mono ? "bsp-row-mono" : ""}`}>
        {value}
      </span>
    </div>
  );
}

/* icons */
function XIcon()       { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function AlertIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function BoxIcon()     { return <i className="bi bi-box-seam" style={{ fontSize: 13, lineHeight: 1 }} />; }
function WeightIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="8" r="4"/><path d="M6 20h12l-2-8H8L6 20z"/></svg>; }
function ProductIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3H8l-2 4h12l-2-4z"/></svg>; }
function VendorIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>; }
function InvoiceIcon() { return <i className="bi bi-receipt" style={{ fontSize: 13, lineHeight: 1 }} />; }